import { useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { ArrowLeft, ChevronLeft, ChevronRight, BarChart3 } from "lucide-react";
import { apiService } from "../services/api";
import SDLAssessmentQuestion from "../components/SDLAssessmentQuestion";
import SDLAssessmentFeedback from "../components/SDLAssessmentFeedback";
import LoadingSpinner from "../components/LoadingSpinner";

interface AnswerState {
  score: number | null;
  details: string;
}

export default function SDLAssessmentResultsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const period = searchParams.get("period") || "";

  const [viewingAnswers, setViewingAnswers] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch product
  const { data: productData, isLoading: productLoading } = useQuery(
    ["product", id],
    () => apiService.getProduct(id!),
    {
      enabled: !!id,
    },
  );

  // Fetch questions
  const { data: questionsData, isLoading: questionsLoading } = useQuery(
    "sdlQuestions",
    apiService.getSDLQuestions,
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  // Fetch assessment for this period
  const {
    data: assessmentData,
    isLoading: assessmentLoading,
    isFetching: assessmentFetching,
  } = useQuery(
    ["sdlAssessment", id, period],
    () => apiService.getSDLAssessmentByPeriod(id!, period),
    {
      enabled: !!id && !!period,
      retry: false,
    },
  );

  // Fetch previous submitted assessment
  const { data: previousAssessmentData } = useQuery(
    ["sdlPreviousAssessment", id, period],
    () =>
      apiService
        .getPreviousSubmittedSDLAssessment(id!, period)
        .catch(() => ({ data: { success: true, data: null } })),
    { enabled: !!id && !!period, retry: false, staleTime: 5 * 60 * 1000 },
  );

  const product = productData?.data?.data;
  const assessment = (assessmentData?.data as any)?.data;
  const previousAssessment = (previousAssessmentData?.data as any)?.data;

  const apiQuestions =
    (((questionsData?.data as any)?.data?.questions || []) as any[]) || [];
  const uiQuestions = useMemo(
    () =>
      apiQuestions.map((q: any) => ({
        id: String(q.id),
        name: q.name,
        description: q.description,
        practice: q.practice,
        criteria: q.criteria || "",
      })),
    [JSON.stringify(apiQuestions.map((q: any) => q.id))],
  );

  // Parse answers (may be array or JSON string)
  const parseAnswersArray = (raw: any): any[] => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return [];
  };

  // Build answers map from assessment data
  const answers: Record<string, AnswerState> = useMemo(() => {
    if (!assessment?.answers || uiQuestions.length === 0) return {};
    const parsed = parseAnswersArray(assessment.answers);
    const map: Record<string, AnswerState> = {};
    uiQuestions.forEach((q) => {
      const found = parsed.find(
        (a: any) => Number(a.questionId) === Number(q.id),
      );
      const hasRealAnswer =
        found &&
        typeof found.maturityScore === "number" &&
        (found.maturityScore !== 0 || (found.details && found.details.trim()));
      map[q.id] = {
        score: hasRealAnswer ? found.maturityScore : null,
        details: found?.details || "",
      };
    });
    return map;
  }, [assessment, uiQuestions]);

  // Build previous answers map
  const previousAnswers: Record<string, AnswerState> = useMemo(() => {
    if (!previousAssessment?.answers || uiQuestions.length === 0) return {};
    const parsed = parseAnswersArray(previousAssessment.answers);
    const map: Record<string, AnswerState> = {};
    uiQuestions.forEach((q) => {
      const found = parsed.find(
        (a: any) => Number(a.questionId) === Number(q.id),
      );
      const hasRealAnswer =
        found &&
        typeof found.maturityScore === "number" &&
        (found.maturityScore !== 0 || (found.details && found.details.trim()));
      map[q.id] = {
        score: hasRealAnswer ? found.maturityScore : null,
        details: found?.details || "",
      };
    });
    return map;
  }, [previousAssessment, uiQuestions]);

  const previousPeriod = previousAssessment?.period || null;

  // Calculate overall score
  const overallScore = useMemo(() => {
    const scored = Object.values(answers).filter((a) => a.score !== null);
    if (scored.length === 0) return 0;
    return scored.reduce((acc, a) => acc + (a.score ?? 0), 0) / scored.length;
  }, [answers]);

  // Loading state — only show spinner while actually fetching data
  const isLoading =
    productLoading ||
    questionsLoading ||
    (!!id && !!period && (assessmentLoading || assessmentFetching));
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Product not found</p>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No assessment found for this period</p>
        <button
          onClick={() => navigate(`/products/${id}`)}
          className="mt-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Product
        </button>
      </div>
    );
  }

  // View Answers mode — read-only question-by-question view
  if (viewingAnswers) {
    const activeQuestion = uiQuestions[activeTab] || uiQuestions[0];
    if (!activeQuestion) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No questions available</p>
        </div>
      );
    }

    return (
      <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-3rem)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          <button
            onClick={() => setViewingAnswers(false)}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
              Assessment Answers
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {product.name} &mdash; {period} (Read Only)
            </p>
          </div>
        </div>

        {/* Question navigation dots */}
        <div className="flex items-center gap-1 justify-center py-2 flex-shrink-0">
          {uiQuestions.map((_: any, i: number) => {
            const ans = answers[uiQuestions[i]?.id || ""];
            const hasScore = ans && ans.score !== null;
            return (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`w-3 h-3 rounded-full transition-all border ${
                  i === activeTab
                    ? "bg-blue-600 border-blue-600 scale-110"
                    : hasScore
                      ? "bg-green-400 border-green-400"
                      : "bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                }`}
                title={`Question ${i + 1}`}
              />
            );
          })}
        </div>

        {/* Question content */}
        <div className="flex-1 overflow-y-auto">
          <SDLAssessmentQuestion
            key={activeQuestion.id}
            index={activeTab}
            question={activeQuestion}
            value={answers[activeQuestion.id]?.score ?? null}
            details={answers[activeQuestion.id]?.details ?? ""}
            onChange={() => {}}
            disabled={true}
            previousAnswer={
              previousAnswers[activeQuestion.id]
                ? {
                    score: previousAnswers[activeQuestion.id]!.score,
                    details: previousAnswers[activeQuestion.id]!.details,
                  }
                : null
            }
            previousPeriod={previousPeriod}
          />
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
            disabled={activeTab === 0}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </button>

          <span className="text-xs text-gray-400 font-medium">
            {activeTab + 1} / {uiQuestions.length}
          </span>

          <button
            onClick={() => {
              if (activeTab < uiQuestions.length - 1) {
                setActiveTab(activeTab + 1);
              } else {
                setViewingAnswers(false);
              }
            }}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            {activeTab < uiQuestions.length - 1 ? (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            ) : (
              <>
                <BarChart3 className="h-4 w-4 mr-1" />
                Back to Results
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Default: Feedback / Results view
  const feedbackAnswers = uiQuestions.map((q: any) => ({
    questionId: q.id,
    name: q.name,
    practice: q.practice,
    score: answers[q.id]?.score ?? 0,
    details: answers[q.id]?.details ?? "",
  }));

  return (
    <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-3rem)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center space-x-3 flex-shrink-0">
        <button
          onClick={() => navigate(`/products/${id}`)}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
            SDL Assessment Results
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {product.name} &mdash; {period}
          </p>
        </div>
      </div>

      <SDLAssessmentFeedback
        productId={id!}
        productName={product.name}
        period={period}
        overallScore={overallScore}
        answers={feedbackAnswers}
        previousAnswers={previousAnswers}
        previousPeriod={previousPeriod}
        onViewAnswers={() => {
          setActiveTab(0);
          setViewingAnswers(true);
        }}
      />
    </div>
  );
}
